/**
 * Three.js 3D Arena & Rendering Manager for Aim Assist Training Lab
 * High performance, capped DPR, responsive resize, and memory leak prevention
 */

import * as THREE from 'three';

export class SceneManager {
  constructor(canvasContainer, settingsStore) {
    this.container = canvasContainer;
    this.settingsStore = settingsStore;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.targetsGroup = null;
    this.weaponGroup = null;
    this.particlePool = [];
    this.activeParticles = [];
    this.isDisposed = false;
    this.resizeObserver = null;
    this.onVisibilityChange = null;

    this.init();
  }

  static isWebGLAvailable() {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (canvas.getContext('webgl2') || canvas.getContext('webgl')));
    } catch {
      return false;
    }
  }

  init() {
    if (!SceneManager.isWebGLAvailable()) {
      return false;
    }

    const width = this.container.clientWidth || 800;
    const height = this.container.clientHeight || 500;

    // 1. Scene - High-visibility esports training arena (Clean Slate / Aim Lab Graybox style)
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x151d2e); // Clean modern cyber slate
    this.scene.fog = new THREE.Fog(0x151d2e, 26, 48); // Gentle distant linear fog (targets 100% sharp & visible)

    // 2. Camera - Accurate Horizontal-to-Vertical FOV projection
    const horFov = Number(this.settingsStore.get('fov')) || 103;
    const aspect = width / height;
    const vertFov = this.computeVerticalFov(horFov, aspect);
    this.camera = new THREE.PerspectiveCamera(vertFov, aspect, 0.1, 100);
    this.camera.position.set(0, 1.65, 0); // Natural eye-level height in meters

    // 3. Renderer with strict performance caps
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const maxDpr = isMobile ? 1.0 : 1.75;
    const dpr = Math.min(window.devicePixelRatio || 1, maxDpr);

    this.renderer = new THREE.WebGLRenderer({
      powerPreference: 'high-performance',
      antialias: true,
      alpha: false,
      stencil: false,
      depth: true
    });
    this.renderer.setSize(width, height, false);
    this.renderer.setPixelRatio(dpr);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;

    this.container.appendChild(this.renderer.domElement);
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.display = 'block';

    // 4. Lighting - Crisp, balanced high-illumination
    this.setupLighting();

    // 5. Arena Environment (Floor, Target Wall, Sci-Fi Accents)
    this.setupArena();

    // 6. Target Group & Weapon Attachment Group
    this.targetsGroup = new THREE.Group();
    this.scene.add(this.targetsGroup);

    this.weaponGroup = new THREE.Group();
    this.camera.add(this.weaponGroup);
    this.scene.add(this.camera);

    // 7. Hit Spark Particle System
    this.setupParticlePool();

    // 8. Responsive ResizeObserver
    this.setupResizeObserver();

    // 9. Visibility Change listener
    this.setupVisibilityListener();

    return true;
  }

  setupLighting() {
    // 1. High ambient illumination for clean target visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.6);
    this.scene.add(ambientLight);

    // 2. Primary overhead target spotlight
    const targetLight = new THREE.DirectionalLight(0xffffff, 1.4);
    targetLight.position.set(0, 10, 5);
    this.scene.add(targetLight);

    // 3. Primary cyan key light
    const keyLight = new THREE.DirectionalLight(0x38bdf8, 1.1);
    keyLight.position.set(8, 12, 6);
    this.scene.add(keyLight);

    // 4. Secondary rim light
    const rimLight = new THREE.DirectionalLight(0x818cf8, 0.9);
    rimLight.position.set(-8, 10, -10);
    this.scene.add(rimLight);

    // 5. Dedicated Weapon Key Light attached to camera (weapon is NEVER a dark silhouette!)
    this.weaponLight = new THREE.PointLight(0xffffff, 2.6, 3.5);
    this.weaponLight.position.set(0.25, 0.15, -0.3);
    this.camera.add(this.weaponLight);

    // 6. Dedicated Weapon Fill Light from the left attached to camera
    const weaponFill = new THREE.PointLight(0x93c5fd, 1.4, 3.0);
    weaponFill.position.set(-0.25, -0.05, -0.3);
    this.camera.add(weaponFill);
  }

  setupArena() {
    // 1. High-Contrast Tactical Cyber Grid Floor
    const floorSize = 64;
    const floorGeo = new THREE.PlaneGeometry(floorSize, floorSize);

    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0f172a'; // Deep slate foundation
    ctx.fillRect(0, 0, 512, 512);

    // Primary grid boundary
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.55)';
    ctx.lineWidth = 3;
    ctx.strokeRect(0, 0, 512, 512);

    // Secondary sub-grid
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.22)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(128, 128, 256, 256);
    ctx.beginPath();
    ctx.moveTo(256, 0); ctx.lineTo(256, 512);
    ctx.moveTo(0, 256); ctx.lineTo(512, 256);
    ctx.stroke();

    const floorTexture = new THREE.CanvasTexture(canvas);
    floorTexture.wrapS = THREE.RepeatWrapping;
    floorTexture.wrapT = THREE.RepeatWrapping;
    floorTexture.repeat.set(16, 16);

    const floorMat = new THREE.MeshStandardMaterial({
      map: floorTexture,
      roughness: 0.45,
      metalness: 0.25
    });

    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = 0;
    this.scene.add(floor);

    // 2. Shooting Range Distance Marker Lines (at 10m, 15m, 20m, 25m)
    const distances = [-10, -15, -20, -25];
    distances.forEach((z) => {
      const lineGeo = new THREE.PlaneGeometry(28, 0.12);
      const lineMat = new THREE.MeshBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.8 });
      const line = new THREE.Mesh(lineGeo, lineMat);
      line.rotation.x = -Math.PI / 2;
      line.position.set(0, 0.02, z);
      this.scene.add(line);
    });

    // 3. Open Curved Stadium Amphitheater (Deep Horizon Arc, NO ugly flat black wall)
    const arcGeo = new THREE.CylinderGeometry(36, 36, 20, 48, 1, true, -Math.PI * 0.75, Math.PI * 1.5);
    const arcMat = new THREE.MeshStandardMaterial({
      color: 0x141f36,
      roughness: 0.65,
      metalness: 0.2,
      side: THREE.BackSide
    });
    const bayArch = new THREE.Mesh(arcGeo, arcMat);
    bayArch.position.set(0, 10, 0);
    this.scene.add(bayArch);

    // 4. Glowing Neon Horizon Accent Rings along the Arc
    [1.65, 4.5, 9.0].forEach((h, idx) => {
      const ringGeo = new THREE.CylinderGeometry(35.8, 35.8, 0.14, 48, 1, true, -Math.PI * 0.75, Math.PI * 1.5);
      const ringMat = new THREE.MeshBasicMaterial({
        color: idx === 0 ? 0x38bdf8 : (idx === 1 ? 0x818cf8 : 0x0ea5e9),
        side: THREE.BackSide
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(0, h, 0);
      this.scene.add(ring);
    });

    // 5. Side Architectural Light Pylons with High-Intensity Neon LED Strips
    [-14, -7, 7, 14].forEach((x) => {
      const pylonGeo = new THREE.BoxGeometry(0.4, 14, 0.4);
      const pylonMat = new THREE.MeshStandardMaterial({
        color: 0x223254,
        roughness: 0.35,
        metalness: 0.6
      });
      const pylon = new THREE.Mesh(pylonGeo, pylonMat);
      pylon.position.set(x, 7, -19);
      this.scene.add(pylon);

      // Vertical LED strip on pylon
      const ledGeo = new THREE.PlaneGeometry(0.12, 12);
      const ledMat = new THREE.MeshBasicMaterial({ color: 0x38bdf8 });
      const led = new THREE.Mesh(ledGeo, ledMat);
      led.position.set(x, 7, -18.78);
      this.scene.add(led);
    });

    // 6. Active Tracers Pool
    this.activeTracers = [];
  }

  addBulletTracer(startVec, endVec, colorHex = 0x38bdf8) {
    if (!this.settingsStore.get('bulletTracers')) return;

    const distance = startVec.distanceTo(endVec);
    if (distance < 0.5) return;

    const beamGeo = new THREE.CylinderGeometry(0.016, 0.016, distance, 6);
    beamGeo.translate(0, distance / 2, 0);
    beamGeo.rotateX(Math.PI / 2);

    const beamMat = new THREE.MeshBasicMaterial({
      color: colorHex,
      transparent: true,
      opacity: 0.95
    });

    const mesh = new THREE.Mesh(beamGeo, beamMat);
    mesh.position.copy(startVec);
    mesh.lookAt(endVec);
    this.scene.add(mesh);

    if (!this.activeTracers) this.activeTracers = [];
    this.activeTracers.push({
      mesh,
      life: 0,
      maxLife: 0.10 // 100ms crisp tracer streak
    });
  }

  updateTracers(delta) {
    if (!this.activeTracers) return;
    for (let i = this.activeTracers.length - 1; i >= 0; i--) {
      const tr = this.activeTracers[i];
      tr.life += delta;
      if (tr.life >= tr.maxLife) {
        this.scene.remove(tr.mesh);
        tr.mesh.geometry.dispose();
        tr.mesh.material.dispose();
        this.activeTracers.splice(i, 1);
      } else {
        tr.mesh.material.opacity = (1 - tr.life / tr.maxLife) * 0.95;
      }
    }
  }

  setupParticlePool() {
    const sparkGeo = new THREE.SphereGeometry(0.045, 6, 6);
    const sparkMat = new THREE.MeshBasicMaterial({ color: 0x38bdf8 });

    for (let i = 0; i < 70; i++) {
      const spark = new THREE.Mesh(sparkGeo, sparkMat.clone());
      spark.visible = false;
      this.scene.add(spark);
      this.particlePool.push({
        mesh: spark,
        velocity: new THREE.Vector3(),
        life: 0,
        maxLife: 0.35
      });
    }
  }

  spawnHitSparks(point, colorHex = 0x38bdf8, count = 10) {
    let spawned = 0;
    for (const p of this.particlePool) {
      if (!p.mesh.visible && spawned < count) {
        p.mesh.position.copy(point);
        p.mesh.material.color.setHex(colorHex);
        p.mesh.visible = true;
        p.life = 0;
        p.maxLife = 0.25 + Math.random() * 0.18;
        p.velocity.set(
          (Math.random() - 0.5) * 5.0,
          (Math.random() - 0.1) * 4.5,
          (Math.random() - 0.5) * 5.0
        );
        this.activeParticles.push(p);
        spawned++;
      }
    }
  }

  updateParticles(delta) {
    this.updateTracers(delta);

    for (let i = this.activeParticles.length - 1; i >= 0; i--) {
      const p = this.activeParticles[i];
      p.life += delta;
      if (p.life >= p.maxLife) {
        p.mesh.visible = false;
        this.activeParticles.splice(i, 1);
      } else {
        p.mesh.position.addScaledVector(p.velocity, delta);
        p.velocity.y -= 9.8 * delta; // Gravity on sparks
        const progress = p.life / p.maxLife;
        const scale = (1 - progress) * 1.3;
        p.mesh.scale.set(scale, scale, scale);
      }
    }
  }

  computeVerticalFov(desiredHorFov, aspect = 16 / 9) {
    const radHor = (desiredHorFov * Math.PI) / 180;
    const radVert = 2 * Math.atan(Math.tan(radHor / 2) / Math.max(0.1, aspect));
    return (radVert * 180) / Math.PI;
  }

  updateFov(desiredHorFov) {
    if (this.camera) {
      const aspect = this.camera.aspect || 16 / 9;
      this.camera.fov = this.computeVerticalFov(desiredHorFov, aspect);
      this.camera.updateProjectionMatrix();
    }
  }

  setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.container);
    } else {
      window.addEventListener('resize', () => this.handleResize());
    }
  }

  handleResize() {
    if (this.isDisposed || !this.renderer || !this.camera || !this.container) return;
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    if (width === 0 || height === 0) return;

    this.camera.aspect = width / height;
    const horFov = Number(this.settingsStore.get('fov')) || 103;
    this.camera.fov = this.computeVerticalFov(horFov, this.camera.aspect);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  setupVisibilityListener() {
    this.onVisibilityChange = () => {
      // Dispatches event handled by training engine to auto-pause simulation
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  render() {
    if (!this.isDisposed && this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  dispose() {
    this.isDisposed = true;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }
  }
}
